<template>
  <div
    data-a4-page
    class="relative flex h-[1754px] w-[1240px] flex-col bg-white"
    style="-webkit-print-color-adjust: exact"
  >
    <!-- Header: name + contact on the left, photo on the right.
         gap-[50px] is Alessandra's 32px Figma minimum at our 1.5625 scale. It
         is a flex gap rather than a margin so a long name can never eat it —
         the text column shrinks and the name breaks instead. -->
    <div class="flex h-[491px] items-start gap-[50px] px-[75px] pt-[100px]">
      <div class="min-w-0 flex-1">
        <p
          class="break-words text-[81px] font-bold uppercase leading-[81px] tracking-[-2px] text-[#1d1d1f]"
        >
          {{ account.firstName }}<br />{{ account.lastName }}
        </p>
        <div class="mt-[25px] h-[6px] w-[200px] bg-[#b8fdff]" />

        <div class="mt-[38px] flex flex-col gap-[19px]">
          <div v-if="account.phone" class="flex items-center gap-[13px]">
            <svg-icon
              name="phone"
              class="h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
            />
            <span
              class="w-[94px] shrink-0 text-[28px] font-bold leading-[42px] text-[#1d1d1f]"
              >Telefon</span
            >
            <span
              class="whitespace-nowrap text-[28px] leading-[42px] text-[#1d1d1f]"
              >{{ account.phone }}</span
            >
          </div>
          <div v-if="account.email" class="flex items-start gap-[13px]">
            <svg-icon
              name="envelope"
              class="mt-[5px] h-[31px] w-[31px] shrink-0 text-[#1d1d1f]"
            />
            <span
              class="w-[94px] shrink-0 text-[28px] font-bold leading-[42px] text-[#1d1d1f]"
              >E-mail</span
            >
            <fitted-email
              :email="account.email"
              :full-size="28"
              :floor-size="19"
              class="leading-[42px] text-[#1d1d1f]"
            />
          </div>
        </div>
      </div>

      <!-- Photo -->
      <div v-if="hasPhoto" class="h-[391px] w-[391px] shrink-0 p-[6px]">
        <div class="h-full w-full overflow-hidden bg-gray-100">
          <img
            v-if="profileImageUrl"
            :src="profileImageUrl"
            class="h-full w-full object-cover"
          />
          <image-preview
            v-else
            :uuid="photoUuid"
            :width="PORTFOLIO_PREVIEW_IMAGE_WIDTH"
            class="h-full w-full object-cover"
          />
        </div>
      </div>
    </div>

    <!-- Bewerbung -->
    <div class="flex h-[606px] flex-col items-center justify-center">
      <p
        class="text-center text-[81px] font-bold uppercase leading-[122px] tracking-[3px] text-[#1d1d1f]"
      >
        Bewerbung
      </p>
      <div class="my-[38px] h-[6px] w-[300px] bg-[#b8fdff]" />
      <p
        class="w-[938px] text-center text-[34px] uppercase leading-[56px] tracking-[0.86px] text-[#1d1d1f]"
      >
        Um einen Praxisplatz im Rahmen eines Dualen Studiums<template
          v-if="formattedIntakeDate"
        >
          ab <span class="font-bold">{{ formattedIntakeDate }}</span></template
        >
      </p>
    </div>

    <!-- Anlagen. Height is left to the content: 491 + 606 + this block lands
         just under the wave at 1363, and the trailing 100px is padding. -->
    <div class="px-[75px] pb-[100px]">
      <p
        class="text-[25px] font-bold uppercase leading-[38px] tracking-[2.3px] text-[#1d1d1f]"
      >
        Anlagen
      </p>
      <div class="mb-[25px] mt-[13px] h-[3px] w-[100px] bg-[#b8fdff]" />
      <div class="flex flex-col gap-[13px]">
        <div
          v-for="attachment in ATTACHMENTS"
          :key="attachment"
          class="relative pl-[25px]"
        >
          <div
            class="absolute left-0 top-[16px] h-[9px] w-[9px] rounded-full bg-[#b8fdff]"
          />
          <p class="text-[25px] leading-[38px] tracking-[0.6px] text-[#1d1d1f]">
            {{ attachment }}
          </p>
        </div>
      </div>
    </div>

    <!-- Footer wave separator -->
    <div class="absolute left-0 top-[1363px] w-full overflow-hidden">
      <svg
        viewBox="0 0 794 57"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        class="w-full"
        preserveAspectRatio="none"
      >
        <path d="M0 55L794 0V57H0Z" fill="#d2fafb" />
      </svg>
    </div>

    <!-- Footer cyan background -->
    <div
      class="absolute bottom-0 left-0 flex h-[306px] w-full bg-[#d2fafb] px-[75px] pt-[75px]"
    >
      <!-- IU Logo -->
      <div class="shrink-0 pt-[4px]">
        <img
          src="@/assets/images/iu-logo-horizontal.svg"
          alt="IU Logo"
          class="h-[65px]"
        />
      </div>

      <!-- Footer text -->
      <div class="ml-[38px] flex flex-col gap-[3px]">
        <p class="text-[25px] leading-[38px] text-[#364153]">
          Bei Fragen zum Dualen Studium wenden Sie sich bitte an die
          Studienberatung der IU Internationalen Hochschule:
        </p>
        <div class="flex items-center gap-[6px]">
          <div class="flex items-center gap-[13px]">
            <svg-icon
              name="envelope"
              class="h-[25px] w-[25px] shrink-0 text-[#364153]"
            />
            <span
              class="text-[25px] font-bold leading-[38px] text-[#364153] underline"
              >unternehmen@iu.org</span
            >
          </div>
          <div class="ml-[12px] flex items-center gap-[13px]">
            <svg-icon
              name="phone"
              class="h-[25px] w-[25px] shrink-0 text-[#364153]"
            />
            <span
              class="text-[25px] font-bold leading-[38px] text-[#364153] underline"
              >+49 36164604050</span
            >
          </div>
        </div>
        <p class="text-[25px] leading-[38px] text-[#364153]">
          Weitere Informationen:
          <span class="underline">www.iu.de/duales-studium/unternehmen</span>
        </p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { format, parseISO } from 'date-fns'
import ImagePreview from '@/components/ImagePreview.vue'
import FittedEmail from './FittedEmail.vue'
import SvgIcon from '@/components/SvgIcon.vue'
import accountStore from '@/stores/account.store'
import documentsStore from '@/stores/documents.store'
import { PORTFOLIO_PREVIEW_IMAGE_WIDTH } from './preview-image.constants'
import { DocumentType } from '@/enums'

const props = withDefaults(
  defineProps<{
    profileImageUrl?: string
    /** Study start of the primary application, as `YYYY-MM-DD`. */
    intakeDate?: string
  }>(),
  { profileImageUrl: undefined, intakeDate: undefined },
)

// Named after the documents themselves. The Figma calls the second one
// "Motivationschreiben", but the page it points at is headed "Persönliche
// Vorstellung" — the list follows the document, not the mock.
const ATTACHMENTS = ['Lebenslauf', 'Persönliche Vorstellung']

const account = computed(() => accountStore.state.account)

const photoUuid = computed(() => {
  const pics = documentsStore.getDocumentsByCategory(DocumentType.Picture)
  return pics.length > 0 ? pics[0].uuid : null
})

const hasPhoto = computed(() => !!props.profileImageUrl || !!photoUuid.value)

// Applicants normally always have an intake. When the chain is missing, the
// whole "ab <date>" clause drops rather than printing a dangling "ab".
const formattedIntakeDate = computed(() => {
  if (!props.intakeDate) return null

  try {
    return format(parseISO(props.intakeDate), 'dd.MM.yyyy')
  } catch {
    return null
  }
})
</script>
